/** QL-287 — Shared number/date formatting for the account cash components (no words). */

import { getMonthName } from "@repo/shared";

/** Bound tenant formatter, from useRegionalFormat(). */
export type FormatNumberFn = (
  value: number | null | undefined,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
) => string;

/** Every amount in the cash components is shown as a whole number (never "-0"). */
export function formatWhole(value: number, fmt: FormatNumberFn): string {
  return fmt(Math.round(value) || 0, { maximumFractionDigits: 0 });
}

/**
 * QL-742 — an account's available cash as a whole amount: rounded DOWN (and
 * never below 0), so moving all of it never needs a deposit. A transfer amount
 * must be whole; the server refuses anything else.
 */
export function wholeAvailableCash(cashLeft: number): number {
  return Math.max(0, Math.floor(cashLeft));
}

/** A deposit is rounded UP, so depositing the shown amount always covers the gap. */
export function formatDeposit(value: number, fmt: FormatNumberFn): string {
  return formatWhole(Math.ceil(Math.round(value * 100) / 100), fmt);
}

/** Signed amount (transfers): +5,000 / −5,000; zero stays a plain 0. */
export function formatSigned(value: number, fmt: FormatNumberFn): string {
  const rounded = Math.round(value);
  if (rounded === 0) return formatWhole(0, fmt);
  return `${rounded > 0 ? "+" : "−"}${formatWhole(Math.abs(rounded), fmt)}`;
}

/**
 * "December 2026" from the portfolio's planned date ("2026-12-01 00:00:00" or
 * ISO). Read from the date part itself, so no time zone can shift the month.
 */
export function formatPlannedMonth(date: string | null, language: string): string {
  const match = date?.match(/^(\d{4})-(\d{2})/);
  if (!match) return "—";
  return `${getMonthName(Number(match[2]) - 1, language, "long")} ${match[1]}`;
}
